import React, { useEffect, useState, useCallback } from 'react';
import DashboardController from '../Controllers/DashboardController';

function formatStatLabel(key) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function StatsBlock({ stats }) {
  if (!stats) return null;

  if (Array.isArray(stats)) {
    return (
      <div className="ivs-otp-legend" style={{ flexWrap: 'wrap', rowGap: 10 }}>
        {stats.map((row, i) => {
          const [labelKey, ...rest] = Object.keys(row);
          return (
            <div className="ivs-otp-legend__item" key={i}>
              <div>
                <span className="ivs-otp-legend__label">{row[labelKey]}</span>
                <span className="ivs-otp-legend__value">{row[rest[0]]}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="ivs-otp-legend" style={{ flexWrap: 'wrap', rowGap: 10 }}>
      {Object.entries(stats).map(([key, value]) => (
        <div className="ivs-otp-legend__item" key={key}>
          <div>
            <span className="ivs-otp-legend__label">{formatStatLabel(key)}</span>
            <span className="ivs-otp-legend__value">{value}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Opportunities() {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchOpportunities = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await DashboardController.getOpportunities();
      setIdeas(data.data || []);
    } catch (err) {
      setError('Failed to load feature opportunities. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOpportunities();
  }, [fetchOpportunities]);

  return (
    <div className="main-panel">
      <div className="content-wrapper ivs-page">
        <div className="d-flex align-items-start justify-content-between mb-4 flex-wrap gap-2">
          <div className="text-left">
            <h2 className="mb-1 font-weight-bold text-left page-heading">Feature Opportunities</h2>
            <p className="text-muted m-0 text-left" style={{ fontSize: 15 }}>
              What the existing database already supports, grounded in live counts
            </p>
          </div>

          <button className="ivs-icon-btn" onClick={fetchOpportunities} disabled={loading} aria-label="Refresh" type="button">
            <i className={`bi ${loading ? 'bi-arrow-repeat spin' : 'bi-arrow-clockwise'}`}></i>
          </button>
        </div>

        {error && (
          <div className="alert alert-danger d-flex align-items-center" role="alert">
            <i className="bi bi-exclamation-triangle mr-2"></i>
            {error}
          </div>
        )}

        {loading ? (
          <div className="ivs-empty-state">Loading…</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {ideas.map((idea) => (
              <div className="ivs-panel" key={idea.key}>
                <div className="ivs-panel__header">
                  <h3 className="ivs-panel__title">{idea.title}</h3>
                </div>

                <p style={{ color: 'var(--ivs-text)', fontSize: 14, lineHeight: 1.6 }}>{idea.description}</p>

                <StatsBlock stats={idea.stats} />

                <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  <div>
                    <div className="ivs-field-label">Tables involved</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {idea.tablesInvolved.map((t) => (
                        <code key={t} style={{ fontSize: 12, background: 'var(--ivs-bg)', padding: '3px 8px', borderRadius: 6, color: 'var(--ivs-text)' }}>
                          {t}
                        </code>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="ivs-field-label">APIs needed</div>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--ivs-text-muted)' }}>
                      {idea.apisNeeded.map((api, i) => (
                        <li key={i} style={{ marginBottom: 4 }}>{api}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
